import React from 'react';
import FeatherIcon from '../FeatherIcon';
import {
  FOUNDERS_THEME,
  estimatedContributionCents,
  formatPrice,
  type Edition,
  type EditionId,
} from '../../products/editions';
import { ProductImageFrame, StatusChip } from './shared';

/**
 * One edition's full listing; the hero CTA and the summary card scroll here. On the Founder's
 * band (`onDark`) every colour comes from the edition theme, since that band is black in both
 * site themes.
 */
const EditionDetail: React.FC<{ edition: Edition; onBuySoon: (id: EditionId) => void; flip?: boolean; onDark?: boolean }> = ({
  edition,
  onBuySoon,
  flip = false,
  onDark = false,
}) => {
  const founders = edition.theme === FOUNDERS_THEME;
  const ink = onDark ? edition.theme.ink : 'text-gray-800';
  const body = onDark ? edition.theme.muted : 'text-gray-700';
  const rule = onDark ? 'border-white/15' : 'border-line';
  const figures: readonly [string, string, string | null][] = [
    ['Proposed retail price', formatPrice(edition.priceCents), null],
    ['Estimated manufacturing cost', formatPrice(edition.estimatedManufacturingCostCents), 'Assumed; real cost will vary.'],
    ['Estimated voucher contribution', formatPrice(estimatedContributionCents(edition)), null],
  ];

  return (
    <article
      id={`edition-${edition.id}`}
      tabIndex={-1}
      aria-labelledby={`edition-${edition.id}-heading`}
      className="scroll-mt-28 focus:outline-none grid gap-8 lg:grid-cols-2 lg:items-start"
    >
      <div className={flip ? 'lg:order-2' : ''}>
        <ProductImageFrame
          image={edition.detailImage}
          label={`${edition.name} product image`}
          theme={edition.theme}
          gold={founders}
          className={onDark ? 'ring-1 ring-[#d4af37]/40' : ''}
        />
      </div>
      <div>
        <StatusChip className={onDark ? `${edition.theme.accent} border-[#d4af37]/50` : 'border-gray-300 text-gray-600'}>
          Prototype · Pre-production
        </StatusChip>
        <h3 id={`edition-${edition.id}-heading`} className={`mt-4 text-3xl font-bold ${ink}`}>
          {edition.name}
        </h3>
        {edition.productionLimit && (
          <p className={`mt-1 font-medium ${edition.theme.accent}`}>
            Proposed production: limited to {edition.productionLimit} individually numbered sets
          </p>
        )}
        <p className={`mt-4 text-lg ${body}`}>{edition.description}</p>

        <dl className={`mt-6 grid sm:grid-cols-3 gap-px overflow-hidden rounded-lg border ${rule}`}>
          {figures.map(([label, value, note]) => (
            <div key={label} className={`p-3 ${onDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <dt className={`text-xs ${body}`}>{label}</dt>
              <dd className={`mt-1 text-lg font-bold tabular-nums ${ink}`}>{value}</dd>
              {note && <dd className={`text-[10px] leading-snug ${body}`}>{note}</dd>}
            </div>
          ))}
        </dl>
        <p className={`mt-2 text-xs ${body}`}>Planning estimates, not guaranteed amounts.</p>

        <h4 className={`mt-6 font-semibold ${ink}`}>What&apos;s included</h4>
        <ul className={`mt-3 grid gap-2 sm:grid-cols-2 text-sm ${body}`}>
          {edition.features.map((feature) => (
            <li key={feature} className="flex gap-2">
              <FeatherIcon
                name="check"
                size={16}
                className={`mt-0.5 shrink-0 ${onDark ? edition.theme.accent : 'text-secondary'}`}
              />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <p className={`mt-6 text-sm ${body}`}>
          <span className={`font-semibold ${ink}`}>Best for:</span> {edition.bestFor}
        </p>
        {edition.note && (
          <p className={`mt-3 flex gap-2 text-sm ${body}`}>
            <FeatherIcon name="info" size={16} className={`mt-0.5 shrink-0 ${onDark ? edition.theme.accent : 'text-primary'}`} />
            <span>{edition.note}</span>
          </p>
        )}
        <button
          type="button"
          onClick={() => onBuySoon(edition.id)}
          className={`mt-6 px-8 py-3 ${onDark ? edition.theme.button : 'btn-primary rounded-full font-medium'}`}
          aria-label={`Buy soon: ${edition.name}. Tell us you would buy it.`}
        >
          Buy soon
        </button>
      </div>
    </article>
  );
};

export default EditionDetail;
